/**
 * Owns the CAD model list for CADViewerPage: the initial fetch (gated on the
 * feature flags), the poll that watches pending/processing models to
 * completion, the two URL-driven auto-select effects (thickness deep links and
 * `?cad_model=<id>`), and the page-level error string those flows share.
 *
 * Extracted from CADViewerPage so the page can stay an orchestrator.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCADModel, listCADModels } from '../../services/cad.service';
import { CADModel } from '../../types/cad';

interface UseCadViewerModelsOptions {
  cadEnabled: boolean | null;
  cadThicknessEnabled: boolean | null;
  thicknessMode: boolean;
  thicknessModelParamId: string | null;
  cadModelParamId: string | null;
}

export interface UseCadViewerModelsResult {
  models: CADModel[];
  setModels: React.Dispatch<React.SetStateAction<CADModel[]>>;
  selectedModel: CADModel | null;
  setSelectedModel: React.Dispatch<React.SetStateAction<CADModel | null>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

export function useCadViewerModels({
  cadEnabled,
  cadThicknessEnabled,
  thicknessMode,
  thicknessModelParamId,
  cadModelParamId,
}: UseCadViewerModelsOptions): UseCadViewerModelsResult {
  const { t } = useTranslation();
  const [models, setModels] = useState<CADModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<CADModel | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load models on mount (only when feature is confirmed enabled)
  const loadModels = useCallback(async () => {
    try {
      const result = await listCADModels();
      setModels(result.models);
    } catch (err: any) {
      setError(t('cad.loadModelsFailed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the load-error message; excluded so a language change doesn't refetch the model list (PR #790)
  }, []);

  useEffect(() => {
    if (
      cadEnabled === true
      && (!thicknessMode || cadThicknessEnabled === true)
    ) {
      loadModels();
    }
  }, [cadEnabled, cadThicknessEnabled, loadModels, thicknessMode]);

  // Thickness deep links stay URL-driven so an in-app link change restores the requested
  // model without remounting the CAD page. Each ?model= value is applied once (when listed):
  // URL writes commit in a router transition, so a click's selection lands first and the
  // lagging URL must not revert it.
  const appliedModelParamRef = useRef<string | null>(null);
  useEffect(() => {
    if (!thicknessMode || !thicknessModelParamId) {
      appliedModelParamRef.current = null;
      return;
    }
    if (appliedModelParamRef.current === thicknessModelParamId) return;
    const found = models.find((model) => model.id === thicknessModelParamId);
    if (!found) return;
    appliedModelParamRef.current = thicknessModelParamId;
    setSelectedModel((current) => (current?.id === found.id ? current : found));
  }, [models, thicknessMode, thicknessModelParamId]);

  // Auto-select the CAD model when arriving via `?cad_model=<id>` (TG #673).
  // One-shot: only the initial resolution runs. Subsequent manual model
  // changes are not overridden even though the URL param still references
  // the original model.
  const cadModelAutoSelectAppliedRef = useRef(false);
  useEffect(() => {
    if (thicknessMode) return;
    if (cadModelAutoSelectAppliedRef.current) return;
    if (!cadModelParamId || models.length === 0) return;
    const found = models.find((m) => m.id === cadModelParamId);
    if (found) {
      setSelectedModel(found);
      cadModelAutoSelectAppliedRef.current = true;
    }
  }, [cadModelParamId, models, thicknessMode]);

  // Poll processing models until they complete
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const processingIds = models
      .filter((m) => m.status === 'processing' || m.status === 'pending')
      .map((m) => m.id);

    if (processingIds.length === 0) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    if (pollingRef.current) return; // already polling

    pollingRef.current = setInterval(async () => {
      for (const id of processingIds) {
        try {
          const updated = await getCADModel(id);
          setModels((prev) =>
            prev.map((m) => {
              if (m.id !== id) return m;
              if (m.status === updated.status) return m;
              return updated;
            })
          );
          setSelectedModel((prev) => {
            if (prev?.id !== id) return prev;
            if (prev.status === updated.status) return prev;
            return updated;
          });
        } catch {
          // ignore polling errors
        }
      }
    }, 3000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [models]);

  return { models, setModels, selectedModel, setSelectedModel, error, setError };
}
